import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, formatPrice, getDeviceId } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Lock, CreditCard } from "lucide-react";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const applied = (() => { try { return JSON.parse(localStorage.getItem("cp_coupon_data")); } catch { return null; } })();
  const discount = applied?.discount || 0;
  const total = Math.max(subtotal - discount, 0);

  const pay = async () => {
    if (!consent) { toast.error("Veuillez accepter le consentement de fourniture immédiate."); return; }
    if (items.length === 0) { toast.error("Votre panier est vide."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: 1 })),
        coupon_code: applied?.code || null,
        consent: true,
        origin_url: window.location.origin,
        device_id: getDeviceId(),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setLoading(false);
    }
  };

  if (items.length === 0) return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center">
      <p className="text-ink-muted">Votre panier est vide.</p>
      <Button asChild className="mt-4 bg-terracotta text-white"><Link to="/boutique">Voir la boutique</Link></Button>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-heading text-4xl text-ink tracking-tight mb-8">Finaliser la commande</h1>
      <div className="bg-cream border border-line rounded-lg p-6 mb-6">
        <h2 className="font-heading text-xl text-ink mb-4">Votre commande</h2>
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.product_id} className="flex justify-between text-sm">
              <span className="text-ink">{it.title}</span>
              <span className="text-ink-muted">{formatPrice(it.price)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-line mt-4 pt-4 space-y-1 text-sm">
          {discount > 0 && <div className="flex justify-between text-sage"><span>Réduction ({applied.code})</span><span>-{formatPrice(discount)}</span></div>}
          <div className="flex justify-between font-semibold text-lg text-ink"><span>Total</span><span>{formatPrice(total)}</span></div>
        </div>
      </div>

      <div className="bg-sand border border-line rounded-lg p-5 mb-6 flex items-start gap-3">
        <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} className="mt-1" data-testid="consent-checkbox" />
        <label htmlFor="consent" className="text-sm text-ink-muted leading-relaxed cursor-pointer">
          Je demande expressément que la fourniture de mon eBook numérique commence immédiatement après mon achat et je reconnais qu'en conséquence, dans les conditions prévues par la réglementation applicable aux contenus numériques sans support matériel, je peux perdre mon droit de rétractation dès le début de la fourniture.
        </label>
      </div>

      <Button onClick={pay} disabled={loading || !consent} size="lg" className="w-full bg-terracotta hover:bg-terracotta-dark text-white rounded-md" data-testid="pay-button">
        {loading ? "Redirection vers le paiement…" : <><Lock className="mr-2 h-4 w-4" /> Payer {formatPrice(total)}</>}
      </Button>

      <div className="flex items-center justify-center gap-6 mt-6 text-xs text-ink-muted">
        <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-sage" /> Paiement 100% sécurisé</span>
        <span className="flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-sage" /> Carte bancaire · Apple Pay</span>
      </div>
      <p className="text-center text-xs text-ink-muted mt-3">Vos données bancaires ne sont jamais stockées sur nos serveurs. Paiement traité par Stripe.</p>
    </div>
  );
}
